import React, { useContext, useEffect, useCallback, useMemo, useRef, useState } from "react";
import AssignmentTurnedInIcon from "@mui/icons-material/AssignmentTurnedIn";
import ContentPasteOffIcon from "@mui/icons-material/ContentPasteOff";
import {
  DataGrid,
  GridToolbarContainer,
  GridToolbarQuickFilter,
} from "@mui/x-data-grid";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import {
  Button,
  CircularProgress,
  IconButton,
  LinearProgress,
  Paper,
  Stack,
  Tooltip,
  Typography,
  TextField,
  darken,
  lighten,
  styled,
  Alert,
  Snackbar,
  Checkbox,
  FormControlLabel,
} from "@mui/material";
import InfoRoundedIcon from "@mui/icons-material/InfoRounded";
import EditIcon from "@mui/icons-material/Edit";
import SaveIcon from "@mui/icons-material/Save";
import CancelIcon from "@mui/icons-material/Cancel";
import STLocationSearchPopup from "./STLocationSearchPopup";
import AddLocationIcon from "@mui/icons-material/AddLocation";
import DoDisturbOnIcon from "@mui/icons-material/DoDisturbOn";
import {
  ALL_TAB,
  getAgeementMatchDataList,
  approveAgreements,
  dismissAgreement,
  updateAgeementMatchLocationIdAndCustomerName,
} from "../services/agreementMatchDataService.js";
import { AuthContext } from "../AuthContext.jsx";
import { useAgreementSyncCount } from "../AgreementSyncCountContext.jsx";
import moment from "moment";
import AgreementSyncDownloadButton from "./AgreementSyncDownloadButton.jsx";
import AgreementSyncDataDownload from "./AgreementSyncDataDownload.jsx";
import AgreementSyncFileUpload from "./AgreementSyncFileUpload.jsx";

const getBackgroundColor = (color, mode) =>
  mode === "dark" ? darken(color, 0.7) : lighten(color, 0.7);

const getHoverBackgroundColor = (color, mode) =>
  mode === "dark" ? darken(color, 0.6) : lighten(color, 0.6);

const StyledDataGrid = styled(DataGrid)(({ theme }) => ({
  "& .super-app-theme--Open": {
    backgroundColor: getBackgroundColor(
      theme.palette.mousehovergrey.main,
      theme.palette.mode
    ),
    "&:hover": {
      backgroundColor: getHoverBackgroundColor(
        theme.palette.mousehovergrey.hover,
        theme.palette.mode
      ),
    },
  },
  "& .row-expired": {
    backgroundColor: theme.palette.mode === "dark" ? "#d59393" : "rgb(237, 195, 195)",
    "&:hover": {
      backgroundColor: theme.palette.mode === "dark" ? "#c57c7c" : "#fbb",
    },
  },
}));

const EditableCell = React.memo(({ value, onSave, onCancel }) => {
  const [localValue, setLocalValue] = useState(value);

  React.useEffect(() => {
    setLocalValue(value ?? "");
  }, [value]);
  return (
    <Stack direction="row" alignItems="center" spacing={1} sx={{ width: "100%" }}>
      <TextField
        size="small"
        variant="standard"
        value={localValue}
        onChange={(e) => setLocalValue(e.target.value)} // local state
        autoFocus
        fullWidth
        onKeyDown={(e) => e.stopPropagation()}
      />
      <IconButton size="small" color="success" onClick={() => onSave(localValue)}>
        <SaveIcon fontSize="small" />
      </IconButton>
      <IconButton size="small" color="error" onClick={onCancel}>
        <CancelIcon fontSize="small" />
      </IconButton>
    </Stack>
  );
});

const CustomToolbar = (props) => {
  const {
    tabType, approveState, pageState, handleApproveAll,
    setFileUploadDialog, handleDownload,
    includeExpiredCancelled, onIncludeExpiredCancelledChange, quickFilter
  } = props;

  // The All tab filters on the server (filterMode="server" below), so its box searches the whole
  // tab and is debounced to one request per pause rather than MUI's 150ms default. The other three
  // still filter only the loaded page in the browser, so no export can match what they show while
  // it has text -- Download is disabled there instead, and says why.
  const isAllTab = tabType === ALL_TAB;
  const pageOnlyFilterActive = !isAllTab && Boolean(quickFilter);

  return (
    <GridToolbarContainer>
      <GridToolbarQuickFilter
        placeholder={isAllTab ? "Search all agreements..." : "Search this page..."}
        debounceMs={isAllTab ? 500 : undefined}
        variant="outlined"
        size="small"
        sx={{ pb: 0 }}
      />
      {/* ... Rest of your toolbar buttons (Approve All, Download, etc.) ... */}
      {(
        <>
          {tabType === "unapproved" && (
            <Button
              variant="contained"
              color="secondary"
              disabled={!approveState.length}
              sx={{ mb: 1 }}
              onClick={handleApproveAll}
            >
              Approve All
            </Button>
          )}
          <Tooltip
            title={pageOnlyFilterActive
              ? "This search filters only the rows on this page, so it can't be exported. Clear it to download the tab."
              : ""}
          >
            {/* A disabled button fires no pointer events, so the tooltip needs a live wrapper. */}
            <span>
              <AgreementSyncDownloadButton
                onDownload={handleDownload}
                disabled={pageState.rows.length === 0 || pageOnlyFilterActive}
              />
            </span>
          </Tooltip>
          {tabType === "nolocation" && (
            <Button
              variant="contained"
              color="primary"
              sx={{ mb: 1 }}
              onClick={() => setFileUploadDialog(true)}
              disabled={pageState.rows.length === 0}
            >
              Upload File
            </Button>
          )}
          {/* Off by default: the All tab's job is the working book, and a dealer's expired
              history can dwarf it. Also hides dismissed rows, which carry the same flag. */}
          {tabType === ALL_TAB && (
            <FormControlLabel
              sx={{ mb: 1, ml: 1 }}
              control={
                <Checkbox
                  size="small"
                  checked={includeExpiredCancelled}
                  onChange={onIncludeExpiredCancelledChange}
                />
              }
              label="Include expired and cancelled"
            />
          )}
        </>
      )}
    </GridToolbarContainer>
  );
};
const AgreementSyncGrid = ({ dealer, dealerName, onEmptyDataList, tabType, onLastSyncDateChange,
  setParentTab, searchAgreementId, setSearchAgreementId }) => {
  const { user, group } = useContext(AuthContext);
  const { refreshOutstandingCount } = useAgreementSyncCount();
  // Held in a ref, not read directly in fetchData. refreshOutstandingCount is a useCallback
  // keyed on [badgeDealerId, group], so depending on it would make fetchData -- and with it
  // the fetch effect below -- change identity every time the badge's dealer settles.
  const refreshCountRef = useRef(refreshOutstandingCount);
  useEffect(() => { refreshCountRef.current = refreshOutstandingCount; }, [refreshOutstandingCount]);
  const [openDialog, setOpenDialog] = useState(false);
  const [selectedAgreement, setSelectedAgreement] = useState(null);
  const [approveState, setApproveStatee] = useState([]);
  const [pageState, setPageState] = useState({
    rows: [],
    rowCount: 0,
  });
  const [loading, setLoading] = useState(true);
  const [paginationModel, setPaginationModel] = useState({
    page: 0,
    pageSize: 50,
  });

  const [dismissLoading, setDismissLoading] = useState({});
  const [startDownload, setStartDownload] = useState(false);
  // "all" tab only. Off by default -- see CustomToolbar.
  const [includeExpiredCancelled, setIncludeExpiredCancelled] = useState(false);
  // The toolbar's quick-filter text, on every tab. Only the All tab sends it to the server; the
  // other three use it to know their Download would not match the page.
  const [quickFilter, setQuickFilter] = useState("");
  const serverQuickFilter = tabType === ALL_TAB ? quickFilter : "";
  // Typing fires a fetch per pause, and a slow response for "ab" must not land after "abc"'s.
  const latestFetch = useRef(0);
  const [fileUploadDialog, setFileUploadDialog] = useState(false);
  const [lastSyncDate, setLastSyncDate] = useState(null);
  const [rowModesModel, setRowModesModel] = useState({});
  const [errorMsg, setErrorMsg] = useState("");
  // NEW STATE: Track which cell is being edited
  const [editCell, setEditCell] = useState({ id: null, field: null, value: "" });
  const fetchData = useCallback(async (pagination) => {
    const fetchId = ++latestFetch.current;
    setLoading(true);
    try {
      const response = await getAgeementMatchDataList(dealer, {
        datatype: searchAgreementId ? "searchAgreement" : "all",
        page: pagination.page,
        limit: pagination.pageSize,
        tabType,
        searchAgreementId,
        includeExpiredCancelled,
        quickFilter: serverQuickFilter,
      });
      if (fetchId !== latestFetch.current) return;

      const json = response.data;
      // The All tab is exempt. Its search reads the All tab's union, which has no tabType to detect and
      // no tab to redirect to -- and without the exemption, a hit here would bounce the user out
      // to Map Location/Pending/Approved on every search, since detectedTab can never equal
      // "all". The tabType guard is belt-and-braces against a handler that starts returning one.
      if (searchAgreementId && tabType !== ALL_TAB && json?.data?.length > 0) {
        const detectedTab = json.data[0].tabType;

        if (detectedTab && detectedTab !== tabType) {
          setParentTab(detectedTab);
          return;
        }
      }

      if (!json || json.totalCount === 0) {
        // onEmptyDataList tells the page the dealer has no agreements, which enables Sync Data. A
        // filter that matches nothing says nothing about that.
        if (!serverQuickFilter) onEmptyDataList(true);
        setPageState({ rows: [], rowCount: 0 });
      } else {
        onEmptyDataList(false);
        const uniqueRows = Array.from(new Map(json.data.map(row => [row.id, row])).values());
        setPageState({ rows: uniqueRows, rowCount: json.totalCount });
        setLastSyncDate(json.lastSyncDate);
      }

      setEditCell({ id: null, field: null, value: "" });
      setRowModesModel({});
    } finally {
      if (fetchId === latestFetch.current) setLoading(false);
      // Keep the nav badge in step with the grid without a page reload. Every action that
      // clears an outstanding agreement -- approve, Approve All, decline, dismiss, mapping a
      // location, an inline edit -- finishes by calling back into fetchData, so refreshing
      // here covers all of them in one place instead of six. It costs one aggregate query
      // (no rows), and only on this screen.
      // Via a ref, so the badge's dealer changing does not re-fetch the grid.
      refreshCountRef.current();
    }
  }, [dealer, tabType, searchAgreementId, onEmptyDataList, includeExpiredCancelled, serverQuickFilter]); // stable deps

  const handleIncludeExpiredCancelledChange = (event) => {
    setIncludeExpiredCancelled(event.target.checked);
    // The result set changes size, so the current offset is meaningless. Without this, ticking
    // the box while on page 7 of a set that then shrinks renders an empty grid that reads as a
    // bug. fetchData's identity changes either way, so the refetch happens regardless.
    setPaginationModel((prev) => (prev.page === 0 ? prev : { ...prev, page: 0 }));
  };

  // The grid owns the filter model; this only reads the quick-filter text out of it. Back to the
  // first page when it changes, for the same reason as the checkbox above.
  const handleFilterModelChange = (model) => {
    const text = (model.quickFilterValues ?? []).join(" ").trim();
    if (text === quickFilter) return;
    setQuickFilter(text);
    if (tabType === ALL_TAB) {
      setPaginationModel((prev) => (prev.page === 0 ? prev : { ...prev, page: 0 }));
    }
  };

  useEffect(() => {
    // Only trigger when the core "data-defining" variables change
    fetchData(paginationModel);
  }, [paginationModel.page, paginationModel.pageSize, fetchData]);

  useEffect(() => {
    onLastSyncDateChange(lastSyncDate);
  }, [lastSyncDate, onLastSyncDateChange]);


  // NEW HANDLER: Save the edited value via API
  // Wrap handleUpdateClick in useCallback
  const handleUpdateClick = useCallback(async (id, field, newValue) => {
    try {
      const row = pageState.rows.find((r) => r.id === id);
      if (!row) return;

      const payload = {
        id,
        tenantId: row.tenantId,
        customerName: field === "customerName" ? newValue : row.customerName,
        locationId: field === "locationId" ? newValue : row.locationId,
      };

      await updateAgeementMatchLocationIdAndCustomerName(id, payload);

      await fetchData(paginationModel);
      setErrorMsg(""); // clear old errors if success
    } catch (error) {
      const message =
        error?.response?.data?.error ||
        error?.message ||
        "Update failed";

      setErrorMsg(message);
      console.error("Update failed:", error);
    }
  }, [pageState.rows, fetchData, paginationModel]);

  // NEW RENDERER: Helper to switch between text and input
  const renderEditableCell = useCallback((params, fieldName) => {
    const isEditing = editCell.id === params.id && editCell.field === fieldName;

    if (isEditing) {
      return (
        <EditableCell
          value={editCell.value}
          onSave={(newValue) => handleUpdateClick(params.id, fieldName, newValue)}
          onCancel={() => setEditCell({ id: null, field: null, value: "" })}
        />
      );
    }

    return (
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ width: "100%" }}>
        <Tooltip title={params.value || ""}>
          <span className="table-cell-trucate">{params.value}</span>
        </Tooltip>
        <IconButton
          size="small"
          onClick={() =>
            setEditCell({
              id: params.id,
              field: fieldName,
              value: params.value || "",
            })
          }
        >
          <EditIcon sx={{ fontSize: 16, opacity: 0.6 }} />
        </IconButton>
      </Stack>
    );
  }, [editCell]); // important

  // ADD THIS RIGHT BELOW
  const processRowUpdate = async (newRow, oldRow) => {
    try {
      await updateAgeementMatchLocationIdAndCustomerName({
        id: newRow.id,
        customerName: newRow.customerName,
        locationId: newRow.locationId,
      });

      return newRow;
    } catch (error) {
      console.error("Update failed:", error);
      return oldRow;
    }
  };
  const columns = [
    { field: "dealerId", headerName: "DealerId", width: 100, headerAlign: "center", align: "center" },
    {
      field: "agreementId",
      headerName: "JBWAgreementId",
      width: 150,
      headerAlign: "center",
      align: "center",
      renderCell: (params) => {
        const purchaseDate = params.row.purchaseDate ? moment.utc(params.row.purchaseDate).format("MM/DD/YYYY") : "NA";
        const startDate = params.row.startDate ? moment.utc(params.row.startDate).format("MM/DD/YYYY") : "NA";
        const expirationDate = params.row.expirationDate ? moment.utc(params.row.expirationDate).format("MM/DD/YYYY") : "NA";
        const tip = `Purchase Date: ${purchaseDate}\nStart Date: ${startDate}\nExpiration Date: ${expirationDate}`;
        return (
          <Tooltip title={<div style={{ whiteSpace: "pre-line" }}>{tip}</div>}>
            <span className="table-cell-trucate">{params.value}</span>
          </Tooltip>
        );
      },
    },
    {
      field: "origin",
      headerName: "Origin",
      width: 120,
      headerAlign: "center",
      align: "center",
      // The handler emits CRM/JB360; the request was phrased in terms of 360 activation, and
      // there is no activation date in the schema for anything finer. See
      // jbwagreementsyncdatalist's allTabArms() for why the split is structural.
      valueFormatter: ({ value }) => (value === "JB360" ? "In 360" : "Pre-360"),
    },
    {
      field: "syncState",
      headerName: "Sync State",
      width: 140,
      headerAlign: "center",
      align: "center",
    },
    {
      // Without this, ticking "Include expired and cancelled" mixed those rows in with nothing
      // to mark them: syncState reads "expired" only for a dismissed sync row, so a cancelled
      // sync row still read "approved" and CRM/JB360 rows read "unmatched"/"jb360".
      // Upper-cased because the All tab passes status through from two sources that disagree on
      // case -- the CRM book writes "cancelled", tblAgreement "CANCELLED" -- for the same state.
      field: "status",
      headerName: "Status",
      width: 120,
      headerAlign: "center",
      align: "center",
      valueFormatter: ({ value }) => (value ? String(value).toUpperCase() : ""),
    },
    {
      field: "customerName",
      headerName: "Customer Name",
      width: group === "jbw-dealer" ? 280 : 200,
      headerAlign: "center",
      align: "center",
      renderCell: (params) => tabType === "approved" && group !== "jbw-dealer"
        ? renderEditableCell(params, "customerName")
        : params.value
    },
    {
      field: "isSentToST",
      headerName: "Synced With ST",
      width: 130,
      headerAlign: "center",
      align: "center",
      renderCell: (params) => (Number(params.value) === 1 ? "Yes" : "No"),
    },
    {
      // Read-only here by design: making the tenant editable (and moving the FSM
      // automations with it) is ADO 331, not this column. Deliberately untyped --
      // tenantId is a bigint identifier, and type: "number" would right-align it
      // and apply locale grouping separators to an id that is not a quantity.
      field: "tenantId",
      headerName: "Tenant Id",
      width: 160,
      headerAlign: "center",
      align: "center",
    },
    {
      field: "locationId",
      headerName: "Location Id",
      width: 160,
      headerAlign: "center",
      align: "center",
      renderCell: (params) => tabType === "approved" && group !== "jbw-dealer"
        ? renderEditableCell(params, "locationId")
        : params.value
    },
    {
      field: "street",
      headerName: "Location",
      width: group === "jbw-dealer" ? 250 : 300,
      headerAlign: "center",
      renderCell: (param) => {
        const locationId = param.row.locationId;
        const tip = param.row.dupicateAgreementIds?.join(",");
        return (
          <>
            {locationId && (
              <Typography variant="subtitle2" component="span" sx={{ wordWrap: "break-word" }} whiteSpace="normal">
                {param.row.street ? `${param.row.street}, ` : ""}
                {param.row.unit ? `${param.row.unit}, ` : ""}
                {param.row.city ? `${param.row.city}, ` : ""}
                {param.row.state ? `${param.row.state}, ` : ""}
                {param.row.zip}.
                {param.row.hasDuplicateLocation === 1 && (
                  <Tooltip title={<div style={{ whiteSpace: "pre-line" }}>Agreements: {tip}</div>}>
                    <InfoRoundedIcon style={{ fill: "#ffc400", marginLeft: 4 }} fontSize="small" />
                  </Tooltip>
                )}
              </Typography>
            )}
          </>
        );
      },
    },
    {
      // The "Location" column above renders nothing unless locationId is set, which is the norm
      // on the All tab -- JB360-created and never-matched agreements have no ST location. This
      // shows the address the agreement actually carries instead.
      field: "fullAddress", headerName: "Address", width: 320, headerAlign: "center",
    },
    { field: "productCode", headerName: "Product Code", width: 130, headerAlign: "center", align: "center" },
    {
      field: "pdfURL",
      headerName: "Agreement Link",
      width: 150,
      headerAlign: "center",
      align: "center",
      renderCell: (params) => (
        <Tooltip title="Open PDF">
          <IconButton color="primary" onClick={() => window.open(params.value, "_blank")}>
            <PictureAsPdfIcon />
          </IconButton>
        </Tooltip>
      ),
    },
    {
      field: "actions",
      headerName: "Action",
      width: 200,
      headerAlign: "center",
      align: "center",
      renderCell: (params) => {
        const locationId = params.row.locationId;
        return (
          <>

            {locationId && tabType === "unapproved" && (
              <>
                <Tooltip title="Approve"><IconButton color="success" onClick={handleApprove(params)}><AssignmentTurnedInIcon /></IconButton></Tooltip>
                <Tooltip title="Decline"><IconButton color="warning" onClick={handleDecline(params)}><ContentPasteOffIcon /></IconButton></Tooltip>
              </>
            )}
            {!locationId && tabType === "nolocation" && (
              <Stack direction="row">
                <Tooltip title="Map Location">
                  <IconButton color="primary" onClick={handleOpenLocationDialog(params)}><AddLocationIcon /></IconButton>
                </Tooltip>

              </Stack>
            )}
            {group !== "jbw-dealer" && (<Tooltip title="Dismiss">
              <span>
                <IconButton
                  color="error"
                  //disabled={params.row.isExpired === 1 || dismissLoading[params.row.id]}
                  disabled={dismissLoading[params.row.id]}
                  onClick={() => {
                    setDismissLoading((prev) => ({ ...prev, [params.row.id]: true }));
                    dismissAgreement(params.row.id, 1)
                      .then(() => fetchData(paginationModel))
                      .finally(() => setDismissLoading((prev) => ({ ...prev, [params.row.id]: false })));
                  }}
                >
                  {dismissLoading[params.row.id] ? <CircularProgress size={20} /> : <DoDisturbOnIcon />}
                </IconButton>
              </span>
            </Tooltip>)}

          </>
        );
      },
    },
  ];

  const handleApprove = (param) => async () => {
    setLoading(true);
    const response = await approveAgreements([param.row.id], user.name);
    if (response.data) {
      setLoading(false);
      fetchData(paginationModel);
    }
  };

  const handleDecline = (param) => () => {
    setOpenDialog(true);
    setSelectedAgreement(param.row);
  };

  const handleOpenLocationDialog = (param) => () => {
    setOpenDialog(true);
    setSelectedAgreement(param.row);
  };

  const handleDialogClose = () => {
    setOpenDialog(false);
    setSelectedAgreement(null);
  };

  const handleGridRefresh = () => {
    fetchData(paginationModel);
    setOpenDialog(false);
    setSelectedAgreement(null);
  };

  const handleApproveAll = async () => {
    setLoading(true);
    const response = await approveAgreements(approveState, user.name);
    if (response.data) {
      setLoading(false);
      fetchData(paginationModel);
    }
  };

  const handleDownload = () => setStartDownload(true);

  const columnVisibilityModel = useMemo(() => ({
    dealerId: group !== "jbw-dealer",
    jbwCustomerName: false,
    jbwPhoneNumber: false,
    jbwEmail: false,
    jbwAddressLine1: false,
    jbwAddressLine2: false,
    jbwCity: false,
    jbwState: false,
    jbwZip: false,
    jbwPurchaseDate: false,
    // The All tab is read-only. Without the second clause the actions column still renders its
    // Dismiss button there, since Dismiss is gated on role rather than on tab.
    actions: tabType !== "approved" && tabType !== ALL_TAB,
    // Emitted only by the All tab's query, so they are meaningless on the other tabs.
    origin: tabType === ALL_TAB,
    syncState: tabType === ALL_TAB,
    status: tabType === ALL_TAB,
    fullAddress: tabType === ALL_TAB,
    street: tabType !== ALL_TAB,
  }), [group, tabType]);
  return (
    <>
      {selectedAgreement && (
        <STLocationSearchPopup
          dialogOpen={openDialog}
          agreement={selectedAgreement}
          onClose={handleDialogClose}
          onGridRefresh={handleGridRefresh}
        />
      )}

      {startDownload && (
        <AgreementSyncDataDownload
          datatype="all"
          tabType={tabType}
          includeExpiredCancelled={includeExpiredCancelled}
          searchAgreementId={searchAgreementId}
          quickFilter={serverQuickFilter}
          openDownload={startDownload}
          onClose={() => setStartDownload(false)}
          dealerId={dealer}
          dealerName={dealerName}
          userName={user.name}
        />
      )}

      {fileUploadDialog && (
        <AgreementSyncFileUpload
          onClose={() => setFileUploadDialog(false)}
          uploadOpen={fileUploadDialog}
          dealerId={dealer}
          dealerName={dealerName}
        />
      )}

      <Stack>
        <Paper elevation={0}>
          {searchAgreementId && !loading && pageState.rows.length === 0 && (
            <Typography color="error" sx={{ p: 2 }}>
              No Agreement found with ID: {searchAgreementId}
            </Typography>
          )}
          <StyledDataGrid
            autoHeight
            pagination
            paginationMode="server"
            filterMode={tabType === ALL_TAB ? "server" : "client"}
            onFilterModelChange={handleFilterModelChange}

            getRowClassName={(params) => {
              if (searchAgreementId) { // apply only when searching
                if (params.row.isExpired === 1 || params.row.isExpired === true) {
                  return "row-expired"; // RED row
                }
                if (params.row.hasDuplicateLocation === 1) {
                  return "super-app-theme--Open"; // existing duplicate highlight
                }
              }
              return ""; // no special styling when not searching
            }}

            checkboxSelection={tabType === "unapproved"}

            rows={pageState.rows}
            rowCount={pageState.rowCount}

            paginationModel={paginationModel}
            onPaginationModelChange={(newModel) =>
              setPaginationModel(newModel)
            }

            loading={loading}
            pageSizeOptions={[20, 50, 100]}

            isRowSelectable={(params) => params.row.locationId}
            onRowSelectionModelChange={(newSelection) =>
              setApproveStatee(newSelection)
            }

            columns={columns}
            disableColumnMenu

            // ADD THESE 4 LINES (IMPORTANT)
            rowModesModel={rowModesModel}
            onRowModesModelChange={setRowModesModel}
            processRowUpdate={processRowUpdate}

            slots={{
              toolbar: CustomToolbar,
              loadingOverlay: LinearProgress,
            }}
            slotProps={{
              toolbar: {
                setSearchAgreementId,
                tabType,
                approveState,
                pageState,
                handleApproveAll,
                setFileUploadDialog,
                handleDownload,
                setParentTab,
                includeExpiredCancelled,
                onIncludeExpiredCancelledChange: handleIncludeExpiredCancelledChange,
                quickFilter,
              },
            }}
            columnVisibilityModel={columnVisibilityModel}
          />
        </Paper>
      </Stack>
      <Snackbar
        open={!!errorMsg}
        autoHideDuration={4000}
        onClose={() => setErrorMsg("")}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert severity="error" onClose={() => setErrorMsg("")}>
          {errorMsg}
        </Alert>
      </Snackbar>
    </>
  );
};

export default AgreementSyncGrid;